'use client';

import { useEffect, useState, useRef, useMemo } from 'react';
import { Info } from 'lucide-react';

import { useCompassContext } from '@/src/libs/CompassContext';
import { Marker, MeetingWithMarkerID } from '@/types.d';
import { flattenMarkerMeetings } from '@/src/utils/info-window-utils/flattenMarkerMeetings';
import { paginate } from '@/src/utils/info-window-utils/paginate';
import { sortMarkersByDistanceFromCenter } from '@/src/utils/map-utils/sortMarkersByDistanceFromCenter';
import InfoMessage from '@/src/components/UI/InfoMessage';

import MeetingCard from '../Cards/MeetingCard';
import Paragraph from '../Typography/Paragraph';
import ErrorMessage from '../UI/ErrorMessage';

import Pagination from './Pagination';
import SearchBar from './SearchBar';
import Filters from './Filters';

const PAGE_SIZE = 10;

interface InfoWindowProps {
  error: Error | null;
  isLoading: boolean;
  markers: Marker[];
  initialLocationSet: boolean;
}

const InfoWindow: React.FC<InfoWindowProps> = ({
  error,
  isLoading,
  markers,
  initialLocationSet,
}) => {
  // The full distance-sorted list, built once; pages are slices of it.
  const [sortedMeetings, setSortedMeetings] = useState<MeetingWithMarkerID[]>(
    []
  );
  const [page, setPage] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const ctx = useCompassContext();

  const pageInfo = useMemo(
    () => paginate(sortedMeetings, page, PAGE_SIZE),
    [sortedMeetings, page]
  );

  useEffect(() => {
    // New focus, markers or initial location: always start from page 1.
    setPage(0);

    if (!ctx || !ctx.state?.map || !markers.length || !initialLocationSet) {
      setSortedMeetings([]);
      return;
    }

    const meetingFocus = markers.find(
      (marker) => marker.markerId === ctx.state.meetingFocus
    );

    if (!meetingFocus) return;

    const sortedMarkers = sortMarkersByDistanceFromCenter(
      meetingFocus.coords,
      markers
    );

    if (!sortedMarkers) return;

    const infoWindowMeetings = flattenMarkerMeetings(sortedMarkers, Infinity);

    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    setSortedMeetings(infoWindowMeetings);
  }, [markers, ctx.state.meetingFocus, initialLocationSet]);

  const handleFocus = (id: string, coords: google.maps.LatLngLiteral) => {
    if (!ctx || !ctx?.state?.map) return;
    ctx?.dispatch({
      type: 'SET_MEETING_FOCUS',
      payload: id,
    });
    ctx.state.map.panTo(coords);
  };

  const goToPage = (nextPage: number) => {
    setPage(nextPage);
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div
      className={`flex flex-col order-1 md:order-0 w-full md:w-[380px] ${
        filtersOpen ? 'h-full' : 'h-3/5'
      } md:h-full bg-white border-t-2 border-black md:border-t-0 md:border-r-2 relative z-10 px-8 py-12 md:py-8`}
    >
      <SearchBar />
      <Filters filtersOpen={filtersOpen} setFiltersOpen={setFiltersOpen} />
      <div data-testid="info-window" className="overflow-auto">
        <div ref={ref} />
        {!isLoading && !markers.length && initialLocationSet && (
          <InfoMessage message="No meetings found in map area." style="mb-8" />
        )}
        {error && <ErrorMessage error={error} style="my-8" />}
        {ctx?.state.meetingFocus &&
          ctx.state.meetingFocus?.indexOf(',') !== -1 &&
          markers.some(
            (marker) =>
              marker.markerId === ctx.state.meetingFocus && !!marker.marker.map
          ) && (
            <div className="rounded-theme border-2 border-black bg-blue mb-8 p-4 flex">
              <Info
                aria-hidden="true"
                className="mr-4 mt-1 shrink-0"
                size={16}
                strokeWidth={1.75}
              />
              <Paragraph
                style="text-sm"
                text="This location hosts multiple meetings"
              />
            </div>
          )}
        {pageInfo.items.map((meeting, ind) => (
          <MeetingCard
            focus={
              !ctx?.state.meetingFocus && ind === 0
                ? true
                : ctx?.state.meetingFocus === meeting.markerId
            }
            key={meeting + '-' + ind.toString()}
            meeting={meeting}
            onClick={() => handleFocus(meeting.markerId, meeting.coords)}
          />
        ))}
      </div>
      {pageInfo.pageCount > 1 && (
        <Pagination
          page={pageInfo.page}
          pageCount={pageInfo.pageCount}
          start={pageInfo.start}
          end={pageInfo.end}
          total={pageInfo.total}
          onPrevious={() => goToPage(pageInfo.page - 1)}
          onNext={() => goToPage(pageInfo.page + 1)}
        />
      )}
    </div>
  );
};

export default InfoWindow;
